import React from 'react';
import { TextField, IconButton, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ExploreIcon from '@mui/icons-material/Explore';
import { StoreFormData } from '../types/Store';

interface StoreFormProps {
  formData: StoreFormData;
  onChange: (field: keyof StoreFormData, value: string) => void;
  errors?: Partial<Record<keyof StoreFormData, string>>;
  onLatLongHelp?: () => void;
}

const StoreForm: React.FC<StoreFormProps> = ({ formData, onChange, errors, onLatLongHelp }) => {
  const handleLatLongPaste = (e: React.ClipboardEvent<HTMLDivElement>, field: 'latitude' | 'longitude') => {
    const pastedText = e.clipboardData.getData('text');
    // Check if pasted text contains a comma (likely "lat, long" format)
    if (pastedText.includes(',')) {
      e.preventDefault();
      const parts = pastedText.split(',').map((p) => p.trim());
      if (parts.length === 2) {
        const [lat, long] = parts;
        // Validate and truncate to 6 decimal places
        const latNum = parseFloat(lat);
        const longNum = parseFloat(long);
        if (!isNaN(latNum) && !isNaN(longNum)) {
          onChange('latitude', latNum.toFixed(6));
          onChange('longitude', longNum.toFixed(6));
        }
      }
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <TextField
          label="Store Name"
          value={formData.name}
          onChange={(e) => onChange('name', e.target.value)}
          fullWidth
          required
          error={!!errors?.name}
          helperText={errors?.name}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          label="Address Line 1"
          value={formData.address_line1}
          onChange={(e) => onChange('address_line1', e.target.value)}
          fullWidth
          required
          error={!!errors?.address_line1}
          helperText={errors?.address_line1}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          label="Address Line 2"
          value={formData.address_line2}
          onChange={(e) => onChange('address_line2', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="City"
          value={formData.city}
          onChange={(e) => onChange('city', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="State/Province"
          value={formData.state}
          onChange={(e) => onChange('state', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Postal Code"
          value={formData.postal_code}
          onChange={(e) => onChange('postal_code', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Country"
          value={formData.country}
          onChange={(e) => onChange('country', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Phone Number"
          value={formData.phone_number}
          onChange={(e) => onChange('phone_number', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="Website"
          value={formData.website}
          onChange={(e) => onChange('website', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField
          label="Latitude"
          value={formData.latitude}
          onChange={(e) => onChange('latitude', e.target.value)}
          onPaste={(e) => handleLatLongPaste(e, 'latitude')}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField
          label="Longitude"
          value={formData.longitude}
          onChange={(e) => onChange('longitude', e.target.value)}
          onPaste={(e) => handleLatLongPaste(e, 'longitude')}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {onLatLongHelp && (
          <Tooltip title="How to get coordinates from Google Maps" arrow>
            <IconButton onClick={onLatLongHelp} color="primary">
              <ExploreIcon sx={{ fontSize: 40 }} />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
};

export default StoreForm;